//@flow
import classnames from 'classnames';
import { EnhancedField } from 'common/components/core/form/Field';
import { HelpTooltip } from 'common/components/core/help';
import { VolumeInput } from 'common/entity/components/volume/VolumeInput';
import { withUnitConverter } from 'common/containers/intl';
import { Carton } from 'common/models/internal/Carton';
import { Volume } from 'common/models/internal/Volume';
import React, { Component } from 'react';
import { themr } from 'react-css-themr';
import { translate } from 'react-i18next';
import { compose } from 'recompose';
import theme from './CartonInput.scss';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import Icon from 'common/components/core/icon';
import { LineItem } from 'common/models/internal/LineItem';
import {
    Dimensions,
    preferredUnitsForDistance,
    sourceUnitForDistance
} from 'common/models/internal/Dimensions';
import Units from 'common/enums/UnitEnum';
import DataUtils from 'common/utils/DataUtils';

type Props = {
    t: (string, ?Object) => string,
    defaultValue: ?Carton,
    theme: Object,
    unitConverter: UnitConversionHelper,
    hiddenCartonSubFields: Array<string>,
    lineItem: LineItem,
    translationsObject: Object
};

type State = {
    carton: Carton
};

class CartonInput extends Component<Props, State> {
    static defaultProps = {
        defaultValue: Carton.fromJS({})
    };

    UNSAFE_componentWillMount() {
        this.setState({
            carton: this.props.defaultValue || Carton.fromJS({})
        });
    }

    componentDidUpdate = prevProps => {
        const { defaultValue } = this.props;
        if (defaultValue && prevProps.defaultValue !== defaultValue) {
            this.setState({
                carton: defaultValue
            });
        }
    };

    handleCartonCapacityChange = (value: string) => {
        this.setState(state => {
            const { carton } = state;
            const updatedCarton = carton.setCapacity(value);
            this.onChange(updatedCarton);
            return {
                carton: updatedCarton
            };
        });
    };

    handleCartonWeightChange = (value: string) => {
        this.setState(state => {
            const { carton } = state;
            const updatedCarton = carton.setWeight(value || null);
            this.onChange(updatedCarton);
            return {
                carton: updatedCarton
            };
        });
    };

    handleCartonVolumeChange = (id: number, volume: Volume) => {
        this.setState(state => {
            const { carton } = state;
            const updatedCarton = carton.setVolume(volume);
            this.onChange(updatedCarton);
            return {
                carton: updatedCarton
            };
        });
    };

    handleCartonDimensionsChange = (id: number, dimensions: Dimensions) => {
        this.setState(state => {
            const { unitConverter } = this.props;
            const { carton } = state;
            const { volume } = carton;
            let updatedCarton = carton;
            if (!dimensions.hasValidationErrors() && dimensions.isFilled()) {
                const sourceUnit = unitConverter.getPreferredUnit({
                    preferredUnits: [
                        Units.list.volume.CubicMeter,
                        Units.list.volume.CubicInch
                    ],
                    sourceUnit: Units.list.volume.CubicMeter
                });
                const targetUnit = unitConverter.getPreferredUnit({
                    sourceUnit: Units.list.volume.CubicMeter
                });
                const updatedVolume = DataUtils.convertTo({
                    value: dimensions.getTotalVolume(),
                    sourceUnit,
                    convertToUnit: targetUnit,
                    decimals: 4,
                    roundOff: true
                });

                updatedCarton = carton.setVolume(
                    Volume.fromJS({ unit: volume.unit, value: updatedVolume })
                );
            }
            updatedCarton = updatedCarton.setDimensions(dimensions);
            this.onChange(updatedCarton);
            return {
                carton: updatedCarton
            };
        });
    };

    onChange = carton => {
        const { onChange } = this.props;
        onChange && onChange(carton);
    };

    renderBoxVolumeOrWeightError(error: ?string) {
        const { theme } = this.props;
        if (error) {
            return (
                <div className={theme.warningMessage}>
                    <Icon icon="alert" theme={theme} />
                    <span className={theme.warningText}>{error}</span>
                </div>
            );
        }
        return null;
    }

    render() {
        const {
            theme,
            t,
            unitConverter,
            hiddenCartonSubFields,
            lineItem,
            translationsObject
        } = this.props;
        const { carton } = this.state;
        const { capacity, weight, volume, dimensions } = carton;
        const preferredWeightUnitText = carton.getPreferredUnitTextForWeight(
            unitConverter
        );
        const distanceUnit = unitConverter.getPreferredUnit({
            preferredUnits: preferredUnitsForDistance,
            sourceUnit: sourceUnitForDistance
        });

        return (
            <div className={classnames(theme.wrap)}>
                {!hiddenCartonSubFields.includes('capacity') ? (
                    <div
                        className={classnames(
                            theme.fieldGroup,
                            theme.halfWidth
                        )}
                    >
                        <span
                            className={classnames(
                                theme.label,
                                theme.subLabel,
                                theme.horizontal
                            )}
                        >
                            {t('skuForm.cartonCapacity')}*
                            <HelpTooltip
                                tooltipText={t('skuForm.quantityInBoxTooltip')}
                            />
                        </span>
                        <EnhancedField
                            type="text"
                            element="input"
                            value={capacity || ''}
                            onChange={this.handleCartonCapacityChange}
                            theme={theme}
                            error={carton.getValidationMessage('capacity')}
                        />
                    </div>
                ) : null}
                {!hiddenCartonSubFields.includes('volume') ? (
                    <div className={theme.fieldGroup}>
                        <VolumeInput
                            translationsObject={translationsObject}
                            id={0}
                            theme={theme}
                            volume={volume}
                            dimensions={dimensions}
                            onChange={this.handleCartonVolumeChange}
                            onDimensionsChange={
                                this.handleCartonDimensionsChange
                            }
                            distanceUnit={distanceUnit}
                            showVolumeUnit
                        />
                        {this.renderBoxVolumeOrWeightError(
                            carton.validateVolumeForParts(
                                lineItem,
                                unitConverter
                            )
                        )}
                    </div>
                ) : null}
                {!hiddenCartonSubFields.includes('weight') ? (
                    <div
                        className={classnames(
                            theme.fieldGroup,
                            theme.halfWidth
                        )}
                    >
                        <span
                            className={classnames(
                                theme.label,
                                theme.subLabel,
                                theme.horizontal
                            )}
                        >
                            {t('skuForm.cartonWeight')}
                            <HelpTooltip
                                tooltipText={t('skuForm.weightInABoxTooltip')}
                            />
                        </span>
                        <EnhancedField
                            type="text"
                            element="input"
                            value={weight}
                            rightIcon={preferredWeightUnitText}
                            onChange={this.handleCartonWeightChange}
                            theme={theme}
                            error={carton.getValidationMessage('weight')}
                        />
                    </div>
                ) : null}
                {this.renderBoxVolumeOrWeightError(
                    carton.validateWeightForParts(lineItem, unitConverter)
                )}
            </div>
        );
    }
}

export default compose(
    themr(null, theme),
    withUnitConverter,
    translate('entityManagement')
)(CartonInput);
